// The device bench (rearch R1, APP_STARTUP "The device bench"): `?bench=1`
// runs ONE fixed, reproducible sequence on any device and reports it — the
// entry (page start → the beam-in overlay gone, i.e. standing), a 60 s walk
// on a fixed street, a 60 s jet flight on a fixed route (a low valley pass, a
// climb, a dive / fast pass over the town) at the real `tunables.json`
// locomotion / glide speeds. The person only holds the phone or wears the
// headset: after the landing's device card the bench enters, walks and flies
// by itself, no input.
//
// The route is WORLD DATA: `client/bench/<meta.region>.json` (build-client
// copies it to dist/bench/; never in tunables.json or world/*.js). Schema 1:
//   { schema: 1, id: "<token>", walk: { s, path: [[x, z], …] },
//     jet: { s, path: [[x, z, y], …] } }
// game metres; the walk starts where the server seats the body (path[0] is
// the entry point), the jet where the walk ended; jet y = the route's
// altitude (m a.s.l.), flown rate-limited by glide.climb_max /
// terminal_fall_speed (what Sim.applyInput accepts), aiming at ≥ JET_AGL_MIN
// over the ground and clamped at JET_FLOOR (each clamp is counted: `floor_n`,
// 0 on an honest route).
//
// Motion is kinematic (the pose is a function of wall time since the phase
// began): a slow device covers the same route in the same 60 s, so the rows
// of two devices see the same world. Off unless asked: main.js holds no
// BenchRun without `?bench=1`.
//
// Pure (no three, no DOM beyond PerformanceObserver): the route follower and
// the stats are what the offline route check and scripts/check-devicebudget.mjs
// exercise.

export const BENCH_PHASES = ['entry', 'walk', 'jet'];
export const BENCH_ID = /^[a-z0-9_-]{1,32}$/;
export const JET_AGL_MIN = 6;        // m over the sampled ground the jet aims to stay above (rate-limited)
export const JET_FLOOR = 0.3;        // m over it the jet is clamped to (counted: floor_n)
const LT_BY = 4;                     // attributed long-task labels per phase

// a bench query (`?bench=1`): true / false
export const benchWanted = (search) => new URLSearchParams(search || '').get('bench') === '1';

// a route file → {id, walk: {s, pts}, jet: {s, pts}}; throws on anything else
export function parseBenchRoute(j) {
  const bad = (m) => { throw new Error(`bench route: ${m}`); };
  if (!j || j.schema !== 1) bad('schema must be 1');
  if (typeof j.id !== 'string' || !BENCH_ID.test(j.id)) bad('id must be a short token');
  const leg = (o, dim, name) => {
    if (!o || !Array.isArray(o.path) || o.path.length < 2) bad(`${name}.path needs ≥ 2 points`);
    const s = o.s;
    if (!(Number.isFinite(s) && s > 0 && s <= 600)) bad(`${name}.s must be 0 < s ≤ 600`);
    for (const p of o.path) {
      if (!Array.isArray(p) || p.length !== dim || !p.every(Number.isFinite)) bad(`${name}.path: [${dim === 2 ? 'x, z' : 'x, z, y'}] numbers`);
    }
    return { s, pts: o.path.map((p) => p.slice()) };
  };
  return { id: j.id, walk: leg(j.walk, 2, 'walk'), jet: leg(j.jet, 3, 'jet') };
}

// a polyline [[x, z, (y)], …] by arc length: at(s) → {x, z, y, hx, hz} (y
// interpolated when the points carry one; hx, hz = the unit heading)
export function polyline(pts) {
  const cum = [0];
  for (let i = 1; i < pts.length; i++) {
    cum.push(cum[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
  }
  const len = cum[cum.length - 1];
  let k = 0;   // the last segment found (s only grows within a phase)
  const at = (s) => {
    s = Math.max(0, Math.min(len, s));
    if (s < cum[k]) k = 0;
    while (k < pts.length - 2 && cum[k + 1] <= s) k++;
    const a = pts[k], b = pts[k + 1];
    const d = cum[k + 1] - cum[k] || 1;
    const f = (s - cum[k]) / d;
    return { x: a[0] + (b[0] - a[0]) * f, z: a[1] + (b[1] - a[1]) * f,
      y: a.length > 2 ? a[2] + (b[2] - a[2]) * f : null,
      hx: (b[0] - a[0]) / d, hz: (b[1] - a[1]) / d };
  };
  return { len, at };
}

// the view yaw that looks along (hx, hz) (camera YXZ, yaw 0 = −z)
export const headingYaw = (hx, hz) => Math.atan2(-hx, -hz);
// a yaw eased toward `want` by at most `rate` rad (the short way round)
function easeYaw(cur, want, rate) {
  const d = Math.atan2(Math.sin(want - cur), Math.cos(want - cur));
  return cur + Math.max(-rate, Math.min(rate, d));
}

// the jet's speeds from tunables (glide + locomotion), m/s
export function jetSpeeds(tun) {
  const g = tun.glide || {}, l = tun.locomotion || {};
  return { v0: l.walk_speed || 0, vmax: (g.max_speed_kmh || 100) / 3.6, accel: g.accel || 10,
    climb: g.climb_max ?? 9, fall: g.terminal_fall_speed ?? 55,
    turn: ((g.stick_turn_rate_deg ?? 90) * Math.PI) / 180 };
}

// one jet frame: the state {s, v, y} advanced by dt along `path`; ground(x, z)
// → m or null. Returns the pose {x, y, z, hx, hz, vy, floored}
export function jetStep(st, path, sp, dt, ground) {
  st.v = Math.min(sp.vmax, st.v + sp.accel * dt);
  st.s = Math.min(path.len, st.s + st.v * dt);
  const p = path.at(st.s);
  const g = ground(p.x, p.z);
  const want = Math.max(p.y, g === null ? -Infinity : g + JET_AGL_MIN);
  const floor = g === null ? -Infinity : g + JET_FLOOR;
  const y0 = st.y;
  st.y += Math.max(-sp.fall * dt, Math.min(sp.climb * dt, want - st.y));
  let floored = false;
  if (st.y < floor) { st.y = floor; floored = true; }
  return { x: p.x, y: st.y, z: p.z, hx: p.hx, hz: p.hz, vy: dt > 0 ? (st.y - y0) / dt : 0, floored };
}

// a sorted sample's q-quantile (nearest rank)
const q = (a, f) => (a.length ? a[Math.min(a.length - 1, Math.floor(a.length * f))] : null);
const r1 = (v) => (v === null || !Number.isFinite(v) ? null : Math.round(v * 10) / 10);

// per-phase accumulators: frame gaps, draw counts, memory, resolution scale
class PhaseStats {
  constructor() {
    this.gaps = []; this.calls = 0; this.callsMax = 0; this.tris = 0; this.trisMax = 0;
    this.heap = null; this.tex = 0; this.geo = 0; this.resMin = null; this.resMax = null;
    this.dist = 0; this.floorN = 0; this.corrN = 0;
  }
  frame(gap, f) {
    this.gaps.push(gap);
    this.calls += f.calls; this.tris += f.tris;
    if (f.calls > this.callsMax) this.callsMax = f.calls;
    if (f.tris > this.trisMax) this.trisMax = f.tris;
    if (f.heap !== null && (this.heap === null || f.heap > this.heap)) this.heap = f.heap;
    if (f.tex > this.tex) this.tex = f.tex;
    if (f.geo > this.geo) this.geo = f.geo;
    if (f.res !== null) {
      if (this.resMin === null || f.res < this.resMin) this.resMin = f.res;
      if (this.resMax === null || f.res > this.resMax) this.resMax = f.res;
    }
  }
}

export class BenchRun {
  // route: parseBenchRoute's; tun: tunables; perf: the PerformanceObserver
  // host (globalThis in the page; absent in a node check)
  // wire: entry → its wire bytes (main.js passes assetq.js wireBytes: a service
  // worker answer's transferSize is 0 on the page, R8a); default transferSize
  constructor(route, tun, wire = (e) => e.transferSize || 0) {
    this.wire = wire;
    this.route = route;
    this.tun = tun;
    this.state = 'entry';            // entry → walk → jet → done
    this.t = { entry: [0, null], walk: [null, null], jet: [null, null] };
    this.stats = { entry: new PhaseStats(), walk: new PhaseStats(), jet: new PhaseStats() };
    this.lts = [];                   // [start, ms, label] every long task (bucketed at the report)
    this.resT = []; this.resE = [];  // resource responseEnd / entry (wire bytes taken at the report: the worker's count lands later)
    this.corr = 0;
    this.yaw = null; this.pitch = 0;
    this._walk = null; this._jet = null; this._jetSt = null; this._last = null;
    this._obs = null;
    if (typeof PerformanceObserver !== 'undefined') {
      try {
        this._obs = new PerformanceObserver((list) => {
          for (const e of list.getEntries()) { this.resT.push(e.responseEnd); this.resE.push(e); }
        });
        this._obs.observe({ type: 'resource', buffered: true });
      } catch { this._obs = null; }
    }
  }
  get driving() { return this.state === 'walk' || this.state === 'jet'; }
  // sched.js onLongTask: every long task with its act label
  longTask(start, ms, label) { if (this.lts.length < 20000) this.lts.push([start, ms, label]); }

  // one frame (main.js tick, before the body moves): gap = the frame interval
  // (ms, unclamped), f = {calls, tris, heap, tex, geo, res}
  frame(gap, f) {
    if (this.state === 'done') return;
    const st = this.stats[this.state];
    if (Number.isFinite(gap) && gap > 0 && gap < 600000) st.frame(gap, f);
  }
  // the overlay went (the player stands and sees): the entry ends, the walk begins
  stand(now, pos) {
    if (this.state !== 'entry') return;
    this.t.entry[1] = now;
    this.t.walk[0] = now;
    this.state = 'walk';
    this._walk = polyline([[pos.x, pos.z], ...this.route.walk.pts]);
    this._last = { x: pos.x, y: pos.y, z: pos.z };
  }
  // the pose for this frame: {x, y, z, vx, vy, vz, speed, state, yaw, pitch}
  // or null (not driving). groundWalk(x, y, z) / groundJet(x, z) → m | null
  drive(now, dt, groundWalk, groundJet) {
    if (!this.driving) return null;
    const lt = this.tun.locomotion || {};
    if (this.state === 'walk' && now - this.t.walk[0] >= this.route.walk.s * 1000) {
      this.t.walk[1] = now; this.t.jet[0] = now; this.state = 'jet';
      const sp = jetSpeeds(this.tun);
      const l = this._last;
      this._jet = polyline([[l.x, l.z, l.y], ...this.route.jet.pts]);
      // the take-off: a JET_FLOOR hop off the walk's stand (within the server's 0.4 m slack)
      this._jetSt = { s: 0, v: sp.v0, y: l.y + JET_FLOOR };
    }
    if (this.state === 'jet' && now - this.t.jet[0] >= this.route.jet.s * 1000) {
      this.t.jet[1] = now; this.state = 'done';
      return null;
    }
    const st = this.stats[this.state];
    const l = this._last;
    let p, speed, state, rate;
    if (this.state === 'walk') {
      const v = lt.walk_speed || 0;
      const a = this._walk.at(v * (now - this.t.walk[0]) / 1000);
      const g = groundWalk(a.x, l.y, a.z);
      p = { x: a.x, y: g === null ? l.y : g, z: a.z, hx: a.hx, hz: a.hz };
      speed = v; state = 'ground';
      rate = ((lt.stick_turn_rate_deg ?? 160) * Math.PI) / 180;
      this.pitch = -0.12;
    } else {
      const sp = jetSpeeds(this.tun);
      p = jetStep(this._jetSt, this._jet, sp, dt, groundJet);
      if (p.floored) st.floorN++;
      speed = this._jetSt.v; state = 'glide'; rate = sp.turn;
      // nose along the flight path, a little under the horizon
      this.pitch = Math.max(-0.9, Math.min(0.5, Math.atan2(p.vy, Math.max(1, speed)) - 0.15));
    }
    const want = headingYaw(p.hx, p.hz);
    this.yaw = this.yaw === null ? want : easeYaw(this.yaw, want, rate * dt);
    const d = dt > 0 ? dt : 1e-3;
    const out = { x: p.x, y: p.y, z: p.z, vx: (p.x - l.x) / d, vy: (p.y - l.y) / d, vz: (p.z - l.z) / d,
      hx: p.hx, hz: p.hz, speed, state, yaw: this.yaw, pitch: this.pitch };
    st.dist += Math.hypot(p.x - l.x, p.z - l.z);
    this._last = { x: p.x, y: p.y, z: p.z };
    return out;
  }
  // a server correction (net.onCorrection) during the run
  correction() { if (this.state !== 'done' && this.state !== 'entry') this.stats[this.state].corrN++; }

  // the `bench` block of the load report (PROTOCOL.md `load`, gs.js benchOfLoad)
  report() {
    const out = { id: this.route.id, done: this.state === 'done' };
    for (const ph of BENCH_PHASES) {
      const [t0, t1] = this.t[ph];
      const s = this.stats[ph];
      if (t0 === null || t1 === null) { out[ph] = null; continue; }
      const g = s.gaps.slice().sort((a, b) => a - b);
      const n = g.length;
      const mean = n ? g.reduce((a, b) => a + b, 0) / n : null;
      // long tasks that STARTED in the window, attributed (sched.js act labels)
      let ltN = 0, ltMax = 0, ltTot = 0, ltMaxL = null;
      const by = new Map();
      for (const [st, ms, l] of this.lts) {
        if (st < t0 || st >= t1) continue;
        ltN++; ltTot += ms;
        if (ms > ltMax) { ltMax = ms; ltMaxL = l; }
        const b = by.get(l) || { n: 0, ms: 0 };
        b.n++; b.ms += ms; by.set(l, b);
      }
      let bytes = 0, nRes = 0;
      for (let i = 0; i < this.resT.length; i++) {
        if (this.resT[i] >= t0 && this.resT[i] < t1) { bytes += this.wire(this.resE[i]); nRes++; }
      }
      out[ph] = {
        t0: Math.round(t0), t1: Math.round(t1), frames: n,
        fps: mean ? r1(1000 / mean) : null, p50: r1(q(g, 0.5)), p95: r1(q(g, 0.95)), p99: r1(q(g, 0.99)),
        worst: r1(n ? g[n - 1] : null),
        lt: { n: ltN, max_ms: Math.round(ltMax), total_ms: Math.round(ltTot), max_l: ltMaxL,
          by: [...by].map(([l, b]) => ({ l, n: b.n, ms: Math.round(b.ms) })).sort((a, b) => b.ms - a.ms).slice(0, LT_BY) },
        calls_max: s.callsMax, calls_mean: n ? Math.round(s.calls / n) : null,
        tris_max: s.trisMax, tris_mean: n ? Math.round(s.tris / n) : null,
        bytes, n_res: nRes,
        heap_mb: s.heap === null ? null : r1(s.heap / 1048576), tex: s.tex, geo: s.geo,
        res_min: s.resMin, res_max: s.resMax,
        dist_m: Math.round(s.dist), floor_n: s.floorN, corr_n: s.corrN,
      };
    }
    return out;
  }
  stop() { if (this._obs) { try { this._obs.disconnect(); } catch { /* gone */ } this._obs = null; } }
}
